import {
  createAssetFromTemplate as createGenericAssetFromTemplate,
  getAssetGroupByContextKey,
  listAssetSubgroupsByGroup,
  listAssetHoldings,
  listAssetsByGroup,
  listAssetTemplatesByGroupKey,
  type AssetOption,
  type AssetSubgroupOption,
} from '@/modules/inventory/holdings'
import type { AssetHolding } from '@/modules/inventory/holdings'

const POULTRY_CONTEXT_KEY = 'farm'
const POULTRY_GROUP_KEY = 'poultry'

const DEFAULT_POULTRY_SUBGROUPS: AssetSubgroupOption[] = [
  { id: 'poultry:chickens', group_id: '', name: 'Galinhas' },
  { id: 'poultry:ducks', group_id: '', name: 'Patos' },
  { id: 'poultry:geese', group_id: '', name: 'Gansos' },
  { id: 'poultry:turkeys', group_id: '', name: 'Perus' },
  { id: 'poultry:quails', group_id: '', name: 'Codornas' },
  { id: 'poultry:guinea-fowl', group_id: '', name: 'Capotes' },
  { id: 'poultry:psittacines', group_id: '', name: 'Psitacídeos' },
  { id: 'poultry:other-birds', group_id: '', name: 'Outras aves' },
]

export async function getPoultryGroupId(): Promise<string> {
  const group = await getAssetGroupByContextKey(POULTRY_CONTEXT_KEY, POULTRY_GROUP_KEY)
  return group.id
}

export async function listPoultryAssets(unitId: string): Promise<AssetOption[]> {
  const groupId = await getPoultryGroupId()
  return listAssetsByGroup(unitId, groupId)
}

export interface PoultryTypeOption {
  value: string
  label: string
  subgroup_id?: string | null
  subgroup_key?: string
  meta?: string
}

export async function listPoultrySubgroups(): Promise<AssetSubgroupOption[]> {
  const groupId = await getPoultryGroupId()
  const subgroups = await listAssetSubgroupsByGroup(groupId)

  if (subgroups.length > 0) return subgroups

  return DEFAULT_POULTRY_SUBGROUPS.map((subgroup) => ({
    ...subgroup,
    group_id: groupId,
  }))
}

export async function getPoultryTypeOptions(unitId: string): Promise<PoultryTypeOption[]> {
  const group = await getAssetGroupByContextKey(POULTRY_CONTEXT_KEY, POULTRY_GROUP_KEY)
  const assets = await listAssetsByGroup(unitId, group.id)
  const usedTemplateIds = assets.map((asset) => asset.template_id).filter(Boolean)
  const templates = await listAssetTemplatesByGroupKey(group.key)
  const subgroups = await listAssetSubgroupsByGroup(group.id)
  const subgroupIdByFallbackId = buildPoultrySubgroupIdMap(subgroups)

  return [
    ...assets.map((asset) => {
      const subgroupKey = derivePoultrySubgroupId(asset.name, asset.attrs)
      return {
        value: asset.id,
        label: asset.name,
        subgroup_key: subgroupKey,
        subgroup_id: resolvePoultrySubgroupId(
        asset.subgroup_id,
        asset.name,
        asset.attrs,
        subgroupIdByFallbackId,
        ),
      }
    }),
    ...templates
      .filter((template) => !usedTemplateIds.includes(template.id))
      .map((template) => {
        const subgroupKey = derivePoultrySubgroupId(template.name, template.attrs)
        return {
          value: `template:${template.id}`,
          label: template.name,
          subgroup_key: subgroupKey,
          subgroup_id: resolvePoultrySubgroupId(
            template.subgroup_id,
            template.name,
            template.attrs,
            subgroupIdByFallbackId,
          ),
        }
      }),
  ]
}

export async function createAssetFromTemplate(unitId: string, templateId: string): Promise<AssetOption> {
  const groupId = await getPoultryGroupId()
  return createGenericAssetFromTemplate({ unitId, templateId, groupId })
}

export async function listPoultryHoldings(unitId: string): Promise<AssetHolding[]> {
  const groupId = await getPoultryGroupId()
  return listAssetHoldings({ unitId, groupId, active: true })
}

function buildPoultrySubgroupIdMap(subgroups: AssetSubgroupOption[]): Record<string, string> {
  const defaultIdByName = DEFAULT_POULTRY_SUBGROUPS.reduce<Record<string, string>>((acc, subgroup) => {
    acc[normalizeSubgroupName(subgroup.name)] = subgroup.id
    return acc
  }, {})

  return subgroups.reduce<Record<string, string>>((acc, subgroup) => {
    const fallbackId = defaultIdByName[normalizeSubgroupName(subgroup.name)]
    if (fallbackId) acc[fallbackId] = subgroup.id
    return acc
  }, {})
}

function resolvePoultrySubgroupId(
  subgroupId: string | null | undefined,
  name: string,
  attrs: Record<string, any> | null | undefined,
  subgroupIdByFallbackId: Record<string, string>,
): string {
  if (subgroupId) return subgroupId

  const fallbackId = derivePoultrySubgroupId(name, attrs)
  return subgroupIdByFallbackId[fallbackId] ?? fallbackId
}

function derivePoultrySubgroupId(name: string, attrs?: Record<string, any> | null): string {
  const storedSubgroupId = String(attrs?.poultry_subgroup_id ?? '')
  if (storedSubgroupId.startsWith('poultry:')) return storedSubgroupId

  const species = String(attrs?.species ?? '').toLowerCase()
  const group = String(attrs?.common_group ?? '').toLowerCase()
  const normalizedName = name
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()

  if (species === 'duck' || normalizedName.includes('pato') || normalizedName.includes('paturi')) {
    return 'poultry:ducks'
  }

  if (species === 'goose' || normalizedName.includes('ganso')) {
    return 'poultry:geese'
  }

  if (species === 'turkey' || normalizedName.includes('peru')) {
    return 'poultry:turkeys'
  }

  if (species === 'quail' || normalizedName.includes('codorna')) {
    return 'poultry:quails'
  }

  if (species === 'guinea_fowl' || normalizedName.includes('capote')) {
    return 'poultry:guinea-fowl'
  }

  if (
    species === 'psittacine' ||
    group === 'macaw' ||
    normalizedName.includes('arara') ||
    normalizedName.includes('papagaio') ||
    normalizedName.includes('periquito') ||
    normalizedName.includes('calopsita')
  ) {
    return 'poultry:psittacines'
  }

  if (species && species !== 'chicken') {
    return 'poultry:other-birds'
  }

  return 'poultry:chickens'
}

function normalizeSubgroupName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
}
